import { useEffect, useState } from "react";
import { Loader2, Save, Mail, ExternalLink, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";

/**
 * Sprint 75 — Admin site settings page.
 *
 * Tiny single-page form for the runtime-tunable knobs that don't deserve
 * their own dashboard:
 *   • micro_hub_radius_km — dispatch hub-preference radius (1-50)
 *   • daily digest — preview + send-now (uses Resend when configured)
 *
 * Lives at /dashboard/site-settings. NOT to be confused with the
 * contact-settings page (which only edits public footer info).
 */
export default function AdminSiteSettings() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [digest, setDigest] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/admin/site-settings")
      .then(({ data }) => alive && setS(data))
      .catch(() => alive && setS(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const save = async () => {
    if (!s) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/site-settings", {
        micro_hub_radius_km: Number(s.micro_hub_radius_km),
      });
      setS(data);
      toast.success("Saved. Dispatch routing updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const toggleOpenAll = async (checked) => {
    setS({ ...s, open_all_pickup_locations: checked });
    try {
      const { data } = await api.put("/admin/site-settings", {
        open_all_pickup_locations: checked,
      });
      setS(data);
      toast.success(checked ? "Pickups now open for ALL locations" : "Pickups restricted to hub service areas");
    } catch (e) {
      setS({ ...s, open_all_pickup_locations: !checked });
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const previewDigest = async () => {
    try {
      const { data } = await api.get("/admin/digest/metrics");
      setDigest(data);
    } catch {
      toast.error("Failed to render digest preview");
    }
  };

  const sendDigestNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/digest/send-now", {});
      if (data.status === "sent") {
        toast.success(`Digest sent to ${data.recipients.length} recipient(s)`);
      } else if (data.status === "skipped") {
        toast.message(`Digest skipped — ${data.reason}`, {
          description: data.recipients?.length
            ? `Would have emailed: ${data.recipients.join(", ")}`
            : undefined,
        });
      } else {
        toast.error(`Digest failed: ${data.error || "unknown"}`);
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-300" /></div>;
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-site-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Operations Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Runtime-editable knobs. No redeploy needed.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[#FF6B00]" /> Pickup availability — all locations
            </h2>
            <p className="text-xs text-slate-500">
              When ON, customers can book pickups from <strong>any location</strong> —
              the hub-radius and state serviceability gates are bypassed. When OFF,
              pickups are limited to active hub service areas.
            </p>
          </div>
          <Switch
            checked={!!s?.open_all_pickup_locations}
            onCheckedChange={toggleOpenAll}
            data-testid="open-all-pickups-toggle"
          />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
        <h2 className="font-semibold text-slate-900 mb-1">Micro-hub preference radius</h2>
        <p className="text-xs text-slate-500 mb-4">
          When a pickup or delivery point falls within this radius of an active
          micro/campus hub, dispatch routes the order through that hub instead
          of the parent main hub. Default 12 km; safe range 1–50 km.
        </p>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min={1}
            max={50}
            step={0.5}
            value={s?.micro_hub_radius_km ?? 12}
            onChange={(e) => setS({ ...s, micro_hub_radius_km: e.target.value })}
            className="w-28 px-3 py-2 text-base rounded-xl border border-slate-200 focus:ring-2 focus:ring-orange-200 focus:outline-none"
            data-testid="micro-hub-radius-input"
          />
          <span className="text-sm text-slate-500">km</span>
          <Button onClick={save} disabled={saving} data-testid="micro-hub-radius-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
            Save
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
        <h2 className="font-semibold text-slate-900 mb-1">Daily ops digest</h2>
        <p className="text-xs text-slate-500 mb-4">
          Sent automatically to founders + CEOs at 08:00 IST every morning.
          Preview yesterday's data or send a one-off right now.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={previewDigest} data-testid="digest-preview">
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Preview metrics
          </Button>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/digest/preview`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center text-sm font-medium px-3 py-2 rounded-xl border border-slate-200 hover:bg-slate-50 min-h-[36px]"
            data-testid="digest-preview-html"
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open HTML preview
          </a>
          <Button size="sm" onClick={sendDigestNow} disabled={sending} data-testid="digest-send-now">
            {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Mail className="h-4 w-4 mr-1.5" />}
            Send now
          </Button>
        </div>

        {digest && (
          <div className="mt-4 rounded-xl bg-slate-50 border border-slate-100 p-4 text-xs" data-testid="digest-metrics">
            <p className="font-semibold text-slate-700 mb-2">{digest.date_label}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Pill label="New orders" v={digest.new_orders} />
              <Pill label="Delivered" v={digest.delivered_count} />
              <Pill label="Revenue" v={`₹${digest.revenue.toLocaleString("en-IN")}`} />
              <Pill label="SLA hit" v={digest.sla_hit_rate != null ? `${digest.sla_hit_rate}%` : "—"} />
              <Pill label="Cancelled" v={digest.cancelled} />
              <Pill label="Refunded" v={digest.refunded} />
              <Pill label="Agent payouts" v={`₹${digest.agent_payouts.toLocaleString("en-IN")}`} />
              <Pill label="New customers" v={digest.new_customers} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Pill({ label, v }) {
  return (
    <div className="bg-white rounded-lg border border-slate-100 p-2.5">
      <div className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">{label}</div>
      <div className="text-base font-bold text-slate-900 mt-0.5">{v}</div>
    </div>
  );
}
